import { useEffect, useState, useCallback } from 'react';
import { AlertOctagon, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../../api/client';
import { Can } from '../../lib/rbac';

/**
 * Cartel "Amazon canceló la compra" (2026-08-26).
 * Ventas VIVAS cuya compra confirmada en Amazon fue cancelada por Amazon y todavía no tienen
 * reposición. Se cierra solo (recompra confirmada / venta cancelada / despachada del depósito)
 * o a mano con "Resuelto".
 */

interface OrderSnap {
  order_status: string | null;
  shipment_status: string | null;
  shipment_substatus: string | null;
  promised_delivery: string | null;
  order_date: string | null;
  site_id: string | null;
  buyer_nickname: string | null;
  purchase_status: string | null;
  purchase_amazon_order_id: string | null;
  purchase_error: string | null;
  purchase_rejection: string | null;
}

interface AlertRow {
  id: number;
  ml_order_id: string;
  asin: string | null;
  title: string | null;
  quantity: number;
  amazon_order_id: string | null;
  unit_cost?: string | null;
  total_cost?: string | null;
  cancelled_detected_at: string;
  repurchase_status: string | null;
  repurchase_detail: string | null;
  order: OrderSnap | null;
}

interface AlertsData {
  total: number;
  alerts: AlertRow[];
}

const REPURCHASE_LABEL: Record<string, string> = {
  pending: 'Recompra pendiente',
  error: 'Recompra falló',
  discarded: 'Recompra descartada',
  needs_review: 'Recompra necesita revisión',
  safeguard_hold: 'Recompra retenida (riesgo de pérdida)',
  pending_approval: 'Recompra esperando aprobación',
};

function humanDetail(a: AlertRow): string {
  const d = a.repurchase_detail || '';
  if (/DELIVERY_SLOW/.test(d) || /Delivery too slow/i.test(d)) {
    const m = d.match(/(\d+)d \(max (\d+)d\)/);
    return m ? `ninguna oferta llega a tiempo (la mejor: ${m[1]} días, máximo ${m[2]})` : 'ninguna oferta llega a tiempo';
  }
  if (/No NEW offer/i.test(d)) return 'Amazon no tiene oferta nueva';
  if (/Loss risk|profit/i.test(d)) return 'la recompra daría pérdida';
  if (/PERDIDA/i.test(d)) return 'paquete dado por perdido';
  return d ? d.slice(0, 120) : '';
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
}

export function AmazonCancelledPanel({ sellerId }: { sellerId: number }) {
  const [data, setData] = useState<AlertsData | null>(null);
  const [open, setOpen] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get<AlertsData>(`/amazon/cancelled-purchase-alerts?seller_id=${sellerId}`);
      if (res.success && res.data) setData(res.data);
      else setData(null);
    } catch {
      setData(null);
    }
  }, [sellerId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!data || data.total === 0) return null;

  const resolve = async (id: number) => {
    const note = window.prompt('¿Cómo se resolvió? (opcional)') ?? '';
    await api.post(`/amazon/cancelled-purchase-alerts/${id}/resolve`, { seller_id: sellerId, note });
    load();
  };

  return (
    <div className="rounded-lg border border-orange-500 bg-orange-50">
      <button
        className="flex w-full flex-wrap items-center gap-2 px-4 py-3 text-left"
        onClick={() => setOpen(!open)}
      >
        <AlertOctagon className="h-5 w-5 text-orange-600" />
        <span className="font-semibold text-orange-800">
          Amazon canceló la compra — {data.total} venta{data.total === 1 ? '' : 's'} sin reposición
        </span>
        <span className="text-sm text-[var(--color-text-secondary)]">
          La compra ya estaba confirmada y Amazon la canceló después; la recompra automática no consiguió producto.
        </span>
        <span className="ml-auto text-[var(--color-text-secondary)]">
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>
      {open && (
        <div className="max-h-96 overflow-y-auto border-t border-orange-200 bg-[var(--color-surface)]">
          {data.alerts.map((a) => {
            const o = a.order;
            const status = a.repurchase_status || 'pending';
            return (
              <div
                key={a.id}
                className="border-b border-[var(--color-border)] px-4 py-3 text-sm text-[var(--color-text)]"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <a
                    href={`https://www.mercadolibre.com/ventas/${a.ml_order_id}/detalle`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 font-mono font-semibold text-[var(--color-primary)] hover:underline"
                  >
                    Venta {a.ml_order_id} <ExternalLink className="h-3 w-3" />
                  </a>
                  {o?.site_id && (
                    <span className="rounded bg-gray-200 px-2 py-0.5 text-xs text-gray-700">{o.site_id}</span>
                  )}
                  <span className="rounded bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800">
                    {REPURCHASE_LABEL[status] || `Recompra: ${status}`}
                  </span>
                  <Can cap="orders.operate">
                    <button
                      onClick={() => resolve(a.id)}
                      className="ml-auto shrink-0 rounded border border-[var(--color-border)] px-2 py-1 text-xs hover:bg-[var(--color-surface-hover)]"
                    >
                      Resuelto
                    </button>
                  </Can>
                </div>
                <div className="mt-1 truncate" title={a.title || ''}>
                  {a.quantity > 1 ? `${a.quantity} × ` : ''}
                  {a.title || a.asin || 'Producto sin título'}
                  {a.asin && <span className="ml-2 font-mono text-xs text-[var(--color-text-secondary)]">{a.asin}</span>}
                </div>
                <div className="mt-1 grid grid-cols-1 gap-x-6 gap-y-0.5 text-xs text-[var(--color-text-secondary)] sm:grid-cols-2">
                  <span>Vendida: {fmtDate(o?.order_date)} · Entrega prometida: {fmtDate(o?.promised_delivery)}</span>
                  <span>
                    Compra cancelada: {a.amazon_order_id || '—'}
                    {a.unit_cost ? ` · US$ ${Number(a.unit_cost).toFixed(2)}` : ''} · detectada {fmtDate(a.cancelled_detected_at)}
                  </span>
                  <span>
                    Envío ML: {o?.shipment_status || '—'}
                    {o?.shipment_substatus ? ` / ${o.shipment_substatus}` : ''}
                  </span>
                  {humanDetail(a) && <span>Motivo: {humanDetail(a)}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
